import { useEffect, useMemo, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Printer, X } from "lucide-react";
import { ThemeContext } from "../../context/ThemeContext";

const BASE_URL = "http://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatDDMMYYYY(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
}

function money2(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

export default function CLAPrintType5() {
  const nav = useNavigate();
  const q = useQuery();
  const { isDark } = useContext(ThemeContext);

  const finYearId = q.get("finYearId") || localStorage.getItem("finYearId") || "";
  const branchId = q.get("branchId") || localStorage.getItem("branchId") || "";
  const branchName = q.get("branchName") || "";
  const depId = q.get("depId") || q.get("depid") || "";
  const depName = q.get("depName") || q.get("dep") || "";
  const depCode = q.get("depCode") || q.get("depcod") || "";
  const fromDate = q.get("fromDate") || q.get("from") || q.get("fdat") || "";
  const toDate = q.get("toDate") || q.get("to") || q.get("tdat") || "";

  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const fetchPrint = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(branchId).trim() || String(branchId) === "Select") {
      toast.warn("Please Select Branch.");
      return;
    }
    if (!String(depId).trim()) {
      toast.warn("Department not found.");
      return;
    }
    if (!fromDate || !toDate) {
      toast.warn("Select Date (both FromDate and ToDate required)");
      return;
    }

    try {
      setLoading(true);

      const fromSafe = String(fromDate).slice(0, 10);
      const toSafe = String(toDate).slice(0, 10);

      const res = await axios.get(`${BASE_URL}/api/cla-allowance/print/type5`, {
        params: {
          finYearId: Number(finYearId),
          branchId: String(branchId),
          branchName: branchName || undefined,
          depId: String(depId),
          depName: depName || undefined,
          depCode: depCode || undefined,
          fromDate: fromSafe,
          toDate: toSafe,
        },
        timeout: 60000,
      });

      if (res?.data?.success) {
        setCompany(res.data.company || null);
        setMeta(res.data.meta || null);
        setRows(res.data.rows || []);
        setTotal(Number(res.data.total || 0));
      } else {
        toast.error(res?.data?.message || "Failed to load print data.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => nav(-1);

  const handlePrint = () => {
    window.print();
  };

  const c = company || {};
  const m = meta || {};

  const companyLines = {
    name: c.companyName || "Nissi Infotech",
    sub: c.category || "CIVIL CONSTRUCTION",
    line1: c.line1 || "3/42-12, Nagappa colony, kavundampalayam",
    city: c.city || "CBE",
    pincode: c.pincode || "641030",
    phone: c.phone || "9003744644",
    state: c.state || "Tamil Nadu",
    country: c.country || "India",
    email: c.email || "infraconsoft-implementation@nissiinfotech.in",
  };

  const title = `CLA REPORT AS ON ${formatDDMMYYYY(m.fromDate || fromDate)} to ${formatDDMMYYYY(
    m.toDate || toDate
  )}`;
  const branchLabel = (m.branchName || branchName || "").trim() || "HEAD-OFFICE";
  const deptLabel = (m.depName || depName || "").trim() || "DEPARTMENT";

  return (
    <>
      {/* Global Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: Arial, Helvetica, sans-serif;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          color-adjust: exact;
        }

        @media print {
          @page {
            size: A4 portrait;
            margin: 10mm;
          }

          html, body {
            width: 210mm;
            height: 297mm;
            margin: 0;
            padding: 0;
          }

          .no-print {
            display: none !important;
          }

          .print-page {
            page-break-after: auto;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            width: 100% !important;
          }

          .outer-border {
            border: 2px solid #000 !important;
          }

          table {
            page-break-inside: auto;
          }

          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          thead {
            display: table-header-group;
          }

          tfoot {
            display: table-footer-group;
          }
        }

        @media screen {
          body {
            background: #e5e7eb;
            padding: 0;
          }

          .print-page {
            width: 210mm;
            min-height: 297mm;
            margin: 20px auto;
            background: white;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            position: relative;
          }
        }

        .outer-border {
          border: 2px solid #000;
          padding: 6mm;
        }

        .header-row {
          display: table;
          width: 100%;
          border-bottom: 2px solid #000;
          margin-bottom: 0;
        }

        .logo-cell {
          display: table-cell;
          width: 25%;
          border-right: 2px solid #000;
          padding: 12px;
          vertical-align: middle;
          text-align: center;
        }

        .logo-text {
          font-weight: 700;
          font-size: 11px;
          line-height: 1.4;
        }

        .company-cell {
          display: table-cell;
          width: 75%;
          padding: 10px 15px;
          vertical-align: middle;
          text-align: center;
        }

        .company-name {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 3px;
        }

        .company-sub {
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 2px;
        }

        .company-line {
          font-size: 11px;
          margin-bottom: 1px;
        }

        .title-bar {
          border-bottom: 2px solid #000;
          text-align: center;
          padding: 8px;
          font-size: 13px;
          font-weight: 700;
        }

        .info-bar {
          border-bottom: 2px solid #000;
          display: table;
          width: 100%;
        }

        .info-cell {
          display: table-cell;
          width: 50%;
          text-align: center;
          padding: 6px 8px;
          font-size: 12px;
          font-weight: 700;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10px;
          margin-top: 0;
        }

        .data-table th,
        .data-table td {
          border: 1px solid #000;
          padding: 5px 6px;
          text-align: left;
        }

        .data-table thead th {
          background: #f0f0f0;
          font-weight: 700;
          text-align: center;
          font-size: 9px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        .text-center {
          text-align: center;
        }

        .text-right {
          text-align: right;
        }

        .total-row td {
          font-weight: 700;
          border-top: 2px solid #000;
        }

        .signature-section {
          border-top: 2px solid #000;
          display: table;
          width: 100%;
          margin-top: 0;
        }

        .signature-cell {
          display: table-cell;
          width: 33.33%;
          padding: 30px 10px 10px;
          font-weight: 700;
          font-size: 11px;
        }

        .signature-cell:not(:first-child) {
          border-left: 1px solid #000;
        }
      `}} />

      <ToastContainer
        position="top-right"
        autoClose={2600}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
        className="no-print"
      />

      {/* Action Buttons - Hidden on print */}
      <div className="no-print" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <button
          onClick={handleClose}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            borderRadius: '12px',
            border: '1px solid #d1d5db',
            background: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            transition: 'all 0.2s'
          }}
        >
          <X size={16} />
          Close
        </button>

        <button
          onClick={handlePrint}
          disabled={loading}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 24px',
            borderRadius: '12px',
            border: 'none',
            background: '#10b981',
            color: 'white',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            opacity: loading ? 0.5 : 1,
            transition: 'all 0.2s'
          }}
        >
          <Printer size={16} />
          Print
        </button>
      </div>

      {/* Top margin for fixed header */}
      <div className="no-print" style={{ height: '80px' }} />

      {/* Loading State */}
      {loading ? (
        <div className="no-print" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#10b981',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      ) : (
        /* Print Document */
        <div className="print-page">
          <div className="outer-border">
            {/* Header */}
            <div className="header-row">
              <div className="logo-cell">
                <div className="logo-text">
                  NISSI<br />
                  INFOTECH PVT<br />
                  LTD
                </div>
              </div>
              <div className="company-cell">
                <div className="company-name">{companyLines.name}</div>
                <div className="company-sub">Nissi</div>
                <div className="company-sub">{companyLines.sub}</div>
                <div className="company-line" style={{ marginTop: '4px' }}>
                  {companyLines.line1}, {companyLines.city} - {companyLines.pincode}, {companyLines.state}, {companyLines.country}
                </div>
                <div className="company-line">
                  Phone No: {companyLines.phone} &nbsp;&nbsp; {companyLines.email}
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="title-bar">{title}</div>

            {/* Info Bar */}
            <div className="info-bar">
              <div className="info-cell">Department: {deptLabel}</div>
              <div className="info-cell">Branch: {branchLabel}</div>
            </div>

            {/* Table */}
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '5%' }}>S.No</th>
                  <th style={{ width: '8%' }}>Emp Code</th>
                  <th style={{ width: '15%' }}>Emp Name</th>
                  <th style={{ width: '15%' }}>Designation</th>
                  <th style={{ width: '12%' }}>Acc-No</th>
                  <th style={{ width: '8%' }}>CLA Days</th>
                  <th style={{ width: '10%' }}>Amount</th>
                  <th style={{ width: '12%' }}>CLA Allowance</th>
                  <th style={{ width: '15%' }}>Signature</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center" style={{ padding: '20px' }}>
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  <>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td className="text-center">{i + 1}</td>
                        <td>{r.EmpCode}</td>
                        <td>{r.EmpName}</td>
                        <td>{r.Designation}</td>
                        <td className="text-center">{r.AccNo}</td>
                        <td className="text-right">{money2(r.CLADays)}</td>
                        <td className="text-right">{money2(r.Amount)}</td>
                        <td className="text-right">{money2(r.CLAAllowance)}</td>
                        <td>&nbsp;</td>
                      </tr>
                    ))}

                    {/* Total Row */}
                    <tr className="total-row">
                      <td colSpan={6}>&nbsp;</td>
                      <td className="text-center">Total</td>
                      <td className="text-right">{money2(total)}</td>
                      <td>&nbsp;</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>

            {/* Signatures */}
            <div className="signature-section">
              <div className="signature-cell">Prepared By</div>
              <div className="signature-cell">Checked By</div>
              <div className="signature-cell">Authorized Signature</div>
            </div>
          </div>
        </div>
      )}

      {/* Spin animation */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}} />
    </>
  );
}